import { Icon, LiveSearchableDropdown, type LiveSearchItem } from '@civilgrid/ui/components';
import { useDebouncedValue } from '@civilgrid/ui/hooks';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { getMyOrganizations } from '~/api/organizations';
import { orgSwitcherOrgsKey } from '~/common/queryKeys';
import { setGlobalFilter } from '~/pages/homeViewV2/store';
import rootStore from '../../../../store';
import { DEBOUNCE_MS, PAGE_SIZE, STALE_TIME } from './constants';

function useOrgPrefetch() {
  const queryClient = useQueryClient();
  useEffect(() => {
    void queryClient.prefetchInfiniteQuery({
      queryKey: orgSwitcherOrgsKey(''),
      queryFn: () => getMyOrganizations('', 0, PAGE_SIZE),
      initialPageParam: 0,
      staleTime: STALE_TIME,
    });
  }, [queryClient]);
}

function useOrgSearch() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, DEBOUNCE_MS);

  const query = useInfiniteQuery({
    queryKey: orgSwitcherOrgsKey(debouncedSearch),
    queryFn: ({ pageParam }) => getMyOrganizations(debouncedSearch, pageParam, PAGE_SIZE),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage.hasMore ? allPages.length : undefined),
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
  });

  const items = useMemo<LiveSearchItem[]>(
    () => query.data?.pages.flatMap((page) => page.items.map((org) => ({ value: org.id, label: org.name }))) ?? [],
    [query.data],
  );

  const hasMore = query.hasNextPage ?? false;

  return {
    items,
    hasMore,
    isLoading: query.isFetching && items.length === 0,
    isLoadingMore: query.isFetchingNextPage,
    search,
    setSearch,
    fetchNextPage: query.fetchNextPage,
  };
}

export type OrgSwitcherProps = {
  inProjectDetail: boolean;
};

export function OrgSwitcher({ inProjectDetail }: OrgSwitcherProps) {
  const navigate = useNavigate();
  const { currentOrganization } = useSnapshot(rootStore) as typeof rootStore;

  useOrgPrefetch();
  const org = useOrgSearch();

  const handleSearchChange = useCallback((search: string) => org.setSearch(search), [org.setSearch]);

  const handleLoadMore = useCallback(() => void org.fetchNextPage(), [org.fetchNextPage]);

  return (
    <LiveSearchableDropdown
      items={org.items}
      hasMore={org.hasMore}
      isLoading={org.isLoading}
      isLoadingMore={org.isLoadingMore}
      onSearchChange={handleSearchChange}
      onLoadMore={handleLoadMore}
      value={currentOrganization?.id}
      onSelect={(item) => {
        setGlobalFilter('');
        rootStore.setCurrentOrganization(item.value);
        void navigate({ to: '/home' });
      }}
      searchPlaceholder="Search organizations..."
      renderTrigger={({ selectedItem }) => (
        <button
          type="button"
          data-testid="top-nav-org-switcher-trigger"
          className="flex h-full text-sm font-noto font-medium text-grey-90 cursor-pointer items-center space-x-2 truncate rounded-[6px] focus-ring"
        >
          <div className={`${inProjectDetail ? 'max-w-[6rem]' : 'max-w-[12rem]'} truncate`}>
            {selectedItem?.label ?? currentOrganization?.name}
          </div>
          {!inProjectDetail && <Icon icon="chevronDown" />}
        </button>
      )}
    />
  );
}
